import { initials } from "../misc/initials";
import { useState } from "react";
import {
  useGetList,
  useListContext,
  useNotify,
  useRefresh,
  useUpdateMany,
} from "ra-core";
import { UserCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import type { Contact, Sale } from "../types";

// Sprint 9.5 — Bulk assign sales_id pour contacts sélectionnés.
// Popover liste des sales actifs.

export function BulkAssignButton() {
  const { selectedIds = [], onUnselectItems } = useListContext<Contact>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [updateMany, { isPending }] = useUpdateMany();
  const [open, setOpen] = useState(false);

  const { data: sales = [] } = useGetList<Sale>(
    "sales",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "first_name", order: "ASC" },
      filter: { disabled: false },
    },
    { enabled: open },
  );

  const applyAssign = (sales_id: number | string) => {
    if (selectedIds.length === 0) return;
    updateMany(
      "contacts",
      { ids: selectedIds, data: { sales_id } },
      {
        onSuccess: () => {
          notify(`Assignation effectuée (${selectedIds.length})`, {
            type: "success",
          });
          onUnselectItems?.();
          refresh();
          setOpen(false);
        },
        onError: (e: unknown) => {
          notify(
            `Échec assignation : ${e instanceof Error ? e.message : "erreur"}`,
            { type: "error" },
          );
        },
      },
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={isPending}
          className="gap-1.5 h-7"
        >
          <UserCheck className="size-3.5" />
          Assigner
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-56 p-1 max-h-72 overflow-y-auto"
      >
        {sales.length === 0 ? (
          <p className="text-[12px] text-muted-foreground px-2 py-1.5">
            Aucun sales actif.
          </p>
        ) : (
          sales.map((sale) => (
            <button
              key={sale.id}
              type="button"
              onClick={() => applyAssign(sale.id)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-[13px] text-foreground hover:bg-fabrik-green-soft transition-colors"
            >
              <span className="size-5 rounded-full bg-fabrik-gray-pill flex items-center justify-center text-[10px] font-semibold text-fabrik-text-2">
                {initials(sale)}
              </span>
              {sale.first_name} {sale.last_name}
            </button>
          ))
        )}
      </PopoverContent>
    </Popover>
  );
}
